import React, { useState, useEffect } from "react";
import { ChefHat, Utensils, Users, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { Loader } from "../components/shared";
import { PaymentsMonitor } from "../components/PaymentsMonitor";
import { DemoMetrics } from "../components/DemoMetrics";
import { FunnelMetrics } from "../components/FunnelMetrics";
import FeedbackAdmin from "../components/FeedbackAdmin";
import AdminOverview from "../components/AdminOverview";
import AdminUsers from "../components/AdminUsers";
import { DEFAULT_FILTERS } from "../lib/adminFilters";
import { SummaryCard } from "../components/WeeklySummaryCard";
import { SaleAlertsCard } from "../components/SaleAlertsCard";
import { PendingAlertsCard } from "../components/PendingAlertsCard";
import AdminAlerts from "../components/AdminAlerts";
import { CurrencyBackfillCard } from "../components/CurrencyBackfillCard";

const Title = ({ children, sub }) => <div className="mb-6"><h1 className="font-display text-2xl text-xmas-snow">{children}</h1>{sub && <p className="mt-1 text-sm text-xmas-champagne">{sub}</p>}</div>;

export default function Admin() {
  return <AdminOverview />;
}

export function AdminAnalytics() {
  return (
    <section data-testid="admin-analytics-page">
      <Title sub="Visitantes, demo, funil, conversões, origem, país, idioma e evolução — a partir dos eventos já registrados.">Analytics</Title>
      <AdminOverview compact />
      <DemoMetrics />
      <FunnelMetrics />
    </section>
  );
}

export function AdminSales() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  return (
    <section data-testid="admin-sales-page">
      <Title sub="Compras, faturamento, ticket médio, vendas por país e por provider (Stripe / Monetizze), transações e status. O filtro de período e país vale para os gráficos, a lista de transações e a exportação CSV.">Vendas & Pagamentos</Title>
      <AdminOverview salesOnly filters={filters} onFiltersChange={setFilters} />
      <div className="my-6"><CurrencyBackfillCard onApplied={() => setFilters((f) => ({ ...f }))} /></div>
      <div className="my-6 grid gap-4 xl:grid-cols-2"><SaleAlertsCard /><PendingAlertsCard /><SummaryCard period="daily" /><SummaryCard period="weekly" /></div>
      <PaymentsMonitor filters={filters} />
    </section>
  );
}

export function AdminUsersPage() { return <AdminUsers />; }

export function AdminAlertsPage() { return <AdminAlerts />; }

export function AdminFeedbackPage() { return <div data-testid="admin-feedback-page"><FeedbackAdmin /></div>; }

export function AdminSettings() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats({})); }, []);
  const cards = stats ? [
    { icon: Users, label: t("admin.users"), value: stats.users },
    { icon: ChefHat, label: t("nav.recipes"), value: stats.content?.recipes },
    { icon: Utensils, label: t("nav.ceia"), value: stats.meal_plans },
  ] : [];
  return (
    <section data-testid="admin-settings-page">
      <Title sub="Provedores de pagamento por país, preços e dados gerais do sistema. Somente leitura/ajuste administrativo — nenhum valor é alterado automaticamente.">Configurações</Title>
      {!stats && <Loader />}
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cards.map((c, i) => (
          <div key={i} className="glass rounded-2xl p-5" data-testid={`admin-stat-${i}`}>
            <c.icon className="mb-3 h-6 w-6 text-xmas-gold" />
            <p className="font-mono-num text-2xl font-bold text-xmas-snow">{c.value ?? 0}</p>
            <p className="text-xs uppercase tracking-wider text-xmas-slate">{c.label}</p>
          </div>
        ))}
      </div>
      <PaymentConfig t={t} />
    </section>
  );
}

function PaymentConfig({ t }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/payment-config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payment-config", {
        checkout_url: cfg.checkout_url, active: cfg.active, validation_status: cfg.validation_status, providers: cfg.providers,
      });
      setCfg({ ...data, markets: cfg.markets, stripe: cfg.stripe });
      toast.success(t("admin.pay.saved"));
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-12" data-testid="admin-payment-config">
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-xmas-gold" />
        <h2 className="font-display text-xl text-xmas-snow">{t("admin.pay.title")}</h2>
      </div>
      <div className="glass rounded-2xl p-6">
        <p className="text-sm text-xmas-slate">{t("admin.pay.desc")}</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-xmas-slate">{t("admin.pay.provider")}</label>
            <input value={cfg.provider} disabled data-testid="pay-provider"
              className="mt-1 w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/40 px-4 py-3 text-sm text-xmas-snow/70 outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-xmas-slate">{t("admin.pay.url")}</label>
            <input value={cfg.checkout_url} placeholder={t("admin.pay.url_ph")} data-testid="pay-url"
              onChange={(e) => setCfg({ ...cfg, checkout_url: e.target.value })}
              className="mt-1 w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-xmas-snow">
              <input type="checkbox" checked={!!cfg.active} data-testid="pay-active"
                onChange={(e) => setCfg({ ...cfg, active: e.target.checked })}
                className="h-4 w-4 accent-xmas-gold" />
              {t("admin.pay.active")}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-xmas-slate">{t("admin.pay.validation")}:</span>
              <select value={cfg.validation_status} data-testid="pay-validation"
                onChange={(e) => setCfg({ ...cfg, validation_status: e.target.value })}
                className="rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-2 text-sm text-xmas-snow outline-none focus:border-xmas-gold">
                <option value="pending">{t("admin.pay.pending")}</option>
                <option value="validated">{t("admin.pay.validated")}</option>
              </select>
            </div>
          </div>
        </div>

        <p className="mt-4 rounded-xl border border-xmas-gold/15 bg-white/5 p-3 text-xs text-xmas-slate">{t("admin.pay.note")}</p>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Object.values(cfg.markets || {}).map((m) => (
            <div key={m.code} className="rounded-lg border border-white/5 bg-xmas-navy/40 px-3 py-2 text-xs text-xmas-snow" data-testid={`pay-market-${m.code}`}>
              {m.flag} {m.name?.pt || m.code} · <span className="text-xmas-champagne">{m.display}</span>
            </div>
          ))}
        </div>

        <ProviderSelector cfg={cfg} setCfg={setCfg} t={t} />

        <button onClick={save} disabled={saving} data-testid="pay-save" className="btn-gold mt-5 rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
          {saving ? "..." : t("admin.pay.save")}
        </button>
      </div>
    </div>
  );
}

// Provider per market: BR locked to Monetizze; international markets can switch to Stripe individually.
function ProviderSelector({ cfg, setCfg, t }) {
  const providers = cfg.providers || {};
  const stripeOk = !!cfg.stripe?.configured;
  const setP = (code, p) => setCfg({ ...cfg, providers: { ...providers, [code]: p } });
  return (
    <div className="mt-6 rounded-2xl border border-xmas-gold/15 bg-white/5 p-4" data-testid="pay-providers">
      <h3 className="text-sm font-semibold text-xmas-snow">{t("admin.pay.providers_title")}</h3>
      <p className="mt-1 text-xs text-xmas-slate">{t("admin.pay.providers_desc")}</p>
      <p className="mt-2 text-xs" data-testid="pay-stripe-status">
        <span className={stripeOk ? "text-emerald-400" : "text-red-400"}>● Stripe {stripeOk ? t("admin.pay.stripe_ready") : t("admin.pay.stripe_missing")}</span>
        {stripeOk && <span className="ml-2 rounded-full bg-xmas-gold/15 px-2 py-0.5 uppercase tracking-wider text-xmas-gold">{cfg.stripe.mode}</span>}
        {cfg.stripe?.webhook_secret_hint && <span className="ml-2 text-xmas-slate" data-testid="pay-stripe-secret-hint">· webhook secret em uso: <span className="font-mono text-xmas-champagne">{cfg.stripe.webhook_secret_hint}</span></span>}
      </p>
      {cfg.stripe?.last_signature_failure && (
        <div className="mt-2 rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-xs" data-testid="pay-stripe-last-failure">
          <p className="font-semibold text-red-300">Última falha de assinatura do webhook Stripe · {new Date(cfg.stripe.last_signature_failure.at).toLocaleString("pt-BR")} · {cfg.stripe.signature_failures_24h} nas últimas 24h</p>
          <p className="mt-1 text-xmas-snow"><span className="font-mono text-red-200">{cfg.stripe.last_signature_failure.code}</span> — {cfg.stripe.last_signature_failure.detail}</p>
          <p className="mt-1 text-xmas-slate">Stripe: "{cfg.stripe.last_signature_failure.stripe_message}" · defasagem {cfg.stripe.last_signature_failure.skew_seconds ?? "—"}s · header {cfg.stripe.last_signature_failure.header_present ? "presente" : "ausente"}</p>
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {Object.values(cfg.markets || {}).map((m) => (
          <div key={m.code} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-xmas-navy/40 px-3 py-2" data-testid={`pay-provider-row-${m.code}`}>
            <span className="text-xs text-xmas-snow">{m.flag} {m.name?.pt || m.code}</span>
            {m.locked ? (
              <span className="rounded-full border border-xmas-gold/30 px-2.5 py-1 text-[11px] uppercase tracking-wider text-xmas-champagne" data-testid={`pay-provider-${m.code}`}>Monetizze · {t("admin.pay.locked")}</span>
            ) : (
              <select value={providers[m.code] || "monetizze"} data-testid={`pay-provider-${m.code}`} onChange={(e) => setP(m.code, e.target.value)}
                className="rounded-lg border border-xmas-gold/20 bg-xmas-navy/60 px-2 py-1 text-xs text-xmas-snow outline-none focus:border-xmas-gold">
                <option value="monetizze">Monetizze</option>
                <option value="stripe" disabled={!stripeOk}>Stripe</option>
              </select>
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-xmas-slate">{t("admin.pay.providers_note")}</p>
    </div>
  );
}
